import { z } from 'zod';
import { patchOf } from './patch';
import { BOARD_KINDS, BOARD_NODE_TYPES } from '../domain';
import { fileDto } from './files';
import { richTextSchema } from './rich-text';

const optionalId = z
  .uuid()
  .nullable()
  .optional()
  .transform((v) => v ?? null);

// Boards (SPEC §14) ----------------------------------------------------------

export const boardInput = z.object({
  title: z.string().trim().min(1, 'Dê um nome ao board').max(160),
  kind: z.enum(BOARD_KINDS).default('board'),
  description: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((v) => v || null),
  projectId: optionalId,
  clientId: optionalId,
  taskId: optionalId,
});
export type BoardInput = z.input<typeof boardInput>;

export const boardPatch = patchOf(boardInput.omit({ kind: true }));

export const boardListQuery = z.object({
  kind: z.enum(BOARD_KINDS).optional(),
  projectId: z.uuid().optional(),
  clientId: z.uuid().optional(),
  taskId: z.uuid().optional(),
  q: z.string().trim().max(120).optional(),
});

export const boardDto = z.object({
  id: z.uuid(),
  kind: z.enum(BOARD_KINDS),
  title: z.string(),
  description: z.string().nullable(),
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  clientId: z.uuid().nullable(),
  clientName: z.string().nullable(),
  clientColor: z.string().nullable(),
  taskId: z.uuid().nullable(),
  nodeCount: z.number(),
  /** Até 4 miniaturas de imagens do board, para o card da lista. */
  preview: z.array(z.string()),
  createdBy: z.string().nullable(),
  updatedAt: z.string(),
  canEdit: z.boolean(),
});
export type BoardDto = z.infer<typeof boardDto>;

// Nós e conexões (SPEC §14–15) -------------------------------------------------

const coord = z.number().finite().min(-1e7).max(1e7);
const MAX_NODE_DATA = 64 * 1024;

/**
 * Conteúdo do nó por tipo (texto, cor, url, paleta…). Guardado como JSON e sempre exibido como
 * texto pela interface (nunca HTML). Links só http(s).
 */
export const nodeData = z
  .record(z.string(), z.unknown())
  .refine((d) => JSON.stringify(d).length <= MAX_NODE_DATA, 'Conteúdo do elemento grande demais')
  .refine(
    (d) => typeof d.url !== 'string' || d.url === '' || /^https?:\/\//i.test(d.url),
    'Link precisa começar com http:// ou https://',
  );

export const boardNodeInput = z.object({
  id: z.uuid(),
  type: z.enum(BOARD_NODE_TYPES),
  x: coord,
  y: coord,
  width: z.number().finite().min(1).max(20_000).nullable(),
  height: z.number().finite().min(1).max(20_000).nullable(),
  rotation: z.number().finite().min(-360).max(360).default(0),
  zIndex: z.number().int().min(-1_000_000).max(1_000_000).default(0),
  parentId: z.uuid().nullable().default(null),
  locked: z.boolean().default(false),
  fileId: z.uuid().nullable().default(null),
  data: nodeData.default({}),
});
export type BoardNodeInput = z.input<typeof boardNodeInput>;
export type BoardNodeDto = z.output<typeof boardNodeInput>;
export const boardNodeDto = boardNodeInput;

export const edgeStyle = z.object({
  color: z.string().max(20).optional(),
  dashed: z.boolean().optional(),
  arrow: z.enum(['none', 'end', 'both']).optional(),
  animated: z.boolean().optional(),
});

export const boardEdgeInput = z.object({
  id: z.uuid(),
  source: z.uuid(),
  target: z.uuid(),
  sourceHandle: z.string().max(40).nullable().default(null),
  targetHandle: z.string().max(40).nullable().default(null),
  label: z.string().trim().max(200).nullable().default(null),
  style: edgeStyle.default({}),
});
export type BoardEdgeDto = z.output<typeof boardEdgeInput>;
export const boardEdgeDto = boardEdgeInput;

/** Lote de mudanças do canvas (gravação por elemento, last-write-wins). */
export const boardOpsInput = z.object({
  /** Conexão de tempo real de quem enviou, para não ecoar a mudança de volta. */
  origin: z.string().max(64).optional(),
  upsertNodes: z.array(boardNodeInput).max(1000).default([]),
  removeNodes: z.array(z.uuid()).max(2000).default([]),
  upsertEdges: z.array(boardEdgeInput).max(2000).default([]),
  removeEdges: z.array(z.uuid()).max(4000).default([]),
});
export type BoardOpsInput = z.input<typeof boardOpsInput>;
export type BoardOps = z.output<typeof boardOpsInput>;

export const boardDetailDto = z.object({
  board: boardDto,
  nodes: z.array(boardNodeDto),
  edges: z.array(boardEdgeDto),
  /** Arquivos usados pelos nós (imagem, vídeo, áudio, arquivo) que a pessoa pode ver. */
  files: z.array(fileDto),
});
export type BoardDetailDto = z.infer<typeof boardDetailDto>;

/** Mensagens de tempo real de um board (WebSocket). */
export type BoardRealtimeMessage =
  | { type: 'board.ops'; boardId: string; origin?: string; by: string; ops: BoardOps }
  | {
      type: 'board.presence';
      boardId: string;
      viewers: { connId: string; userId: string; name: string; avatarUrl: string | null }[];
    }
  | { type: 'board.cursor'; boardId: string; connId: string; x: number; y: number }
  | { type: 'board.deleted'; boardId: string };

// Notas (Creative → Notes) ------------------------------------------------------

export const noteInput = z.object({
  title: z.string().trim().max(200).default(''),
  content: richTextSchema.nullable().optional(),
  projectId: optionalId,
  clientId: optionalId,
});
export const notePatch = patchOf(noteInput);

export const noteDto = z.object({
  id: z.uuid(),
  title: z.string(),
  content: richTextSchema.nullable(),
  excerpt: z.string(),
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  clientId: z.uuid().nullable(),
  clientName: z.string().nullable(),
  createdBy: z.string().nullable(),
  updatedAt: z.string(),
  canEdit: z.boolean(),
});
export type NoteDto = z.infer<typeof noteDto>;
